import { AppealButton } from "./Rewards.js";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { Campaign, GameSession, TranscriptLine } from "@dm/shared";
import { api } from "./api.js";
import { speakerColor } from "./colors.js";
import { useGame } from "./game.js";

const POLL_MS = 2000;
type Pick = "all" | "none" | number;

const fmtDate = (iso: string) => new Date(iso).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
const fmtTime = (iso: string) => new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

/** Transcript with a session picker; past sessions show their summary and "Previously on…" recap (editable by staff). */
export function TranscriptPage({ campaign }: { campaign: Campaign }) {
  const { sessionsVersion, playSession } = useGame();
  const [sessions, setSessions] = useState<GameSession[]>([]);
  const [pick, setPick] = useState<Pick | null>(null);

  useEffect(() => {
    api.sessions(campaign.id).then((r) => {
      setSessions(r.sessions);
      // Default: tonight's session if one is running, else everything.
      setPick((p) => p ?? (r.current ? r.current.id : "all"));
    }, () => setPick((p) => p ?? "all"));
  }, [campaign.id, sessionsVersion]);

  const selected = typeof pick === "number" ? sessions.find((s) => s.id === pick) ?? null : null;
  // Poll for new lines unless we're looking at a finished session.
  const live = pick === "all" || pick === "none" || (selected !== null && !selected.endedAt) || (typeof pick === "number" && playSession?.id === pick);

  return (
    <main className="transcript">
      <div className="transcript-head">
        <h2>Transcript</h2>
        <label className="field inline">
          <span>Session</span>
          <select value={pick === null ? "" : String(pick)} onChange={(e) => setPick(e.target.value === "all" || e.target.value === "none" ? e.target.value : Number(e.target.value))}>
            <option value="all">All lines</option>
            {sessions.map((s) => (
              <option key={s.id} value={s.id}>
                Session {s.number} · {fmtDate(s.startedAt)}{s.endedAt ? "" : " (in progress)"}
              </option>
            ))}
            <option value="none">Outside sessions</option>
          </select>
        </label>
      </div>
      <TypeBox campaign={campaign} />
      {selected && <SessionInfo key={selected.id} session={selected} campaignId={campaign.id} onChange={(s) => setSessions((list) => list.map((x) => (x.id === s.id ? s : x)))} />}
      {pick !== null && <Lines key={String(pick)} campaignId={campaign.id} pick={pick} live={live} />}
    </main>
  );
}

function SessionInfo({ session, campaignId, onChange }: { session: GameSession; campaignId: number; onChange: (s: GameSession) => void }) {
  const { isHost, fail } = useGame();
  const [editing, setEditing] = useState<"recap" | "summary" | null>(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      onChange(await api.editSession(campaignId, session.id, editing === "recap" ? { recap: draft } : { summary: draft }));
      setEditing(null);
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };
  const status = {
    none: "", pending: "The DM's scribe is writing the summary…", done: "", failed: "The summary couldn't be written automatically; you can write one.", skipped: "Too short to summarise.",
  }[session.summaryStatus];
  const block = (field: "recap" | "summary", label: string, value: string | null, hint: string) => (
    <div className="session-text">
      <div className="row-between">
        <strong>{label}</strong>
        {isHost && editing !== field && (
          <button className="small" onClick={() => { setEditing(field); setDraft(value ?? ""); }}>
            Edit
          </button>
        )}
      </div>
      {editing === field ? (
        <>
          <textarea rows={field === "recap" ? 4 : 8} value={draft} maxLength={field === "recap" ? 3000 : 8000} onChange={(e) => setDraft(e.target.value)} />
          <div className="quick-row">
            <button className="primary" disabled={busy} onClick={save}>Save</button>
            <button disabled={busy} onClick={() => setEditing(null)}>Cancel</button>
            <span className="muted small">{hint}</span>
          </div>
        </>
      ) : value ? (
        <p className="prewrap">{value}</p>
      ) : (
        <p className="muted small">{status || "Not written yet."}</p>
      )}
    </div>
  );
  return (
    <section className="panel session-info">
      <p className="muted small">
        Session {session.number}: {fmtDate(session.startedAt)} {fmtTime(session.startedAt)}
        {session.endedAt ? ` – ${fmtTime(session.endedAt)}` : " (in progress)"}
        {session.startedByName ? ` · started by ${session.startedByName}` : ""}
        {session.endReason ? ` · ended by ${{ leave: "/leave", auto_leave: "auto-leave (empty channel)", button: "End Session", stale: "inactivity", update: "a server update" }[session.endReason]}` : ""}
      </p>
      {session.endedAt && (
        <>
          {block("recap", "\u201cPreviously on…\u201d recap", session.recap, "Read aloud when the next session starts.")}
          {session.recapEditedByName && <p className="muted small">Recap edited by {session.recapEditedByName}</p>}
          {block("summary", "Summary", session.summary, "Also saved to campaign memory.")}
        </>
      )}
    </section>
  );
}

function Lines({ campaignId, pick, live }: { campaignId: number; pick: Pick; live: boolean }) {
  const { rewoundLines } = useGame();
  const [lines, setLines] = useState<TranscriptLine[]>([]);
  const [error, setError] = useState<string | null>(null);
  const lastId = useRef(0);
  const top = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLOListElement>(null);
  const prevHeight = useRef(0);
  const [away, setAway] = useState(false);
  const liveRef = useRef(live);
  liveRef.current = live;

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const session = pick === "all" ? null : pick;
    const tick = async () => {
      try {
        // Catch up in pages of 500, then poll.
        for (let page = 0; page < 40; page++) {
          const res = await api.transcript(campaignId, lastId.current, session, 500);
          if (cancelled) return;
          if (res.lines.length) {
            lastId.current = res.lines[res.lines.length - 1]!.id;
            setLines((prev) => [...prev, ...res.lines].slice(-5000));
          }
          if (res.lines.length < 500) break;
        }
        setError(null);
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      }
      if (!cancelled && liveRef.current) timer = setTimeout(tick, POLL_MS);
    };
    void tick();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [campaignId, pick]);

  // Newest lines are at the top. If you've scrolled down to read, keep your place as new lines arrive above.
  useLayoutEffect(() => {
    const el = list.current;
    if (!el) return;
    const h = el.scrollHeight;
    const grown = h - prevHeight.current;
    prevHeight.current = h;
    if (grown > 0 && el.getBoundingClientRect().top < 0) window.scrollBy(0, grown);
  }, [lines.length]);

  useEffect(() => {
    const onScroll = () => setAway((list.current?.getBoundingClientRect().top ?? 0) < -200);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const newestFirst = useMemo(() => [...lines].reverse(), [lines]);

  return (
    <>
      {error && <p className="error">Connection problem: {error}</p>}
      {lines.length === 0 && <p className="muted">Nothing yet. Use /join in Discord and start talking.</p>}
      <div ref={top} />
      {away && <button className="jump-latest" onClick={() => top.current?.scrollIntoView({ behavior: "smooth" })}>↑ Latest</button>}
      <ol ref={list} className="newest-first">
        {newestFirst.map((l) => {
          const system = l.source === "system" || (!l.isDm && !l.speakerDiscordId && l.speakerName === "System");
          const color = l.isDm || system ? undefined : speakerColor(l.speakerDiscordId ?? l.speakerName);
          return (
            <li
              key={l.id}
              className={`${l.isDm ? "dm" : system ? "system" : "player"}${l.rewound || rewoundLines.has(l.id) ? " rewound" : ""}`}
              style={color ? { color } : undefined}
              title={l.rewound || rewoundLines.has(l.id) ? "Rewound by the Host: the DM no longer remembers this" : undefined}
            >
              <time>{new Date(l.createdAt).toLocaleTimeString()}</time>
              <strong>{l.speakerName}</strong>
              <span>
                {l.source === "text" && <span className="tag typed" title="Typed on the web table">typed</span>}
                {l.text}
                {l.isDm && !system && !l.rewound && !rewoundLines.has(l.id) && <AppealButton lineId={l.id} text={l.text} />}
              </span>
            </li>
          );
        })}
      </ol>
    </>
  );
}

const TYPED_MAX = 500;
/** Type a line into the transcript (newest lines are at the top, so the box is too). */
function TypeBox({ campaign }: { campaign: Campaign }) {
  const [text, setText] = useState("");
  // "To the DM" is on by default (most typed lines are for the DM: a line typed without it used to go to table chat
  // only, and players re-typed it). The choice is remembered per campaign on this device.
  const toDmKey = `dm-typed-to-dm-${campaign.id}`;
  const [toDm, setToDmState] = useState(() => localStorage.getItem(toDmKey) !== "0");
  const setToDm = (v: boolean) => {
    setToDmState(v);
    localStorage.setItem(toDmKey, v ? "1" : "0");
  };
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const addressed = /^\s*(?:hey\s+|ok(?:ay)?\s+)?(?:dungeon\s*master|dm)\b/i.test(text);
  const goesToDm = !campaign.addressedOnly || toDm || addressed;
  const send = async () => {
    const t = text.trim();
    if (!t || busy) return;
    setBusy(true);
    setNote(null);
    try {
      await api.sayTyped(campaign.id, t, toDm);
      setText("");
    } catch (err) {
      setNote((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="type-box panel" onSubmit={(e) => { e.preventDefault(); void send(); }}>
      <div className="row">
        <input aria-label="Type a line" value={text} maxLength={TYPED_MAX} disabled={busy} style={{ flex: 1 }}
          placeholder={campaign.addressedOnly ? "Type to the table (start with \u201cDM,\u201d to ask the Dungeon Master)" : "Type to the Dungeon Master"}
          onChange={(e) => setText(e.target.value)} />
        {campaign.addressedOnly && (
          <label className="inline small" title="Send this line to the Dungeon Master, like saying \u201cDungeon Master, \u2026\u201d">
            <input type="checkbox" checked={toDm} onChange={(e) => setToDm(e.target.checked)} /> To the DM
          </label>
        )}
        <button type="submit" disabled={busy || !text.trim()}>{goesToDm ? "Send to DM" : "Send"}</button>
      </div>
      <p className="muted small">
        {goesToDm ? "The Dungeon Master will answer in voice." : "Table chat: everyone sees it; the Dungeon Master doesn't."}
        {text.length > TYPED_MAX - 80 ? ` ${TYPED_MAX - text.length} characters left.` : ""}
        {note && <span className="danger"> {note}</span>}
      </p>
    </form>
  );
}
